/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { bind } from 'bind-event-listener';
import { useIntl } from 'react-intl';

import { IconButton } from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import type { EmojiProvider } from '@atlaskit/emoji';
import { ResourcedEmoji } from '@atlaskit/emoji/element';
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import Form, { Field } from '@atlaskit/form';
import ViewAllIcon from '@atlaskit/icon/core/align-text-left';
import CheckMarkIcon from '@atlaskit/icon/core/check-mark';
import CrossIcon from '@atlaskit/icon/core/cross';
import DeleteIcon from '@atlaskit/icon/core/delete';
import EditIcon from '@atlaskit/icon/core/edit';
import FolderClosedIcon from '@atlaskit/icon/core/folder-closed';
import MoreIcon from '@atlaskit/icon/core/show-more-horizontal';
import { ButtonItem, MenuGroup } from '@atlaskit/menu';
import { fg } from '@atlaskit/platform-feature-flags';
import { Popup } from '@atlaskit/popup';
import { Box } from '@atlaskit/primitives/compiled';
import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';
import type { Space } from '@atlassian/rovo-spaces/controllers/space-store/types';
import { useDeleteSpace } from '@atlassian/rovo-spaces/services/useDeleteSpace';
import { useListSpaces } from '@atlassian/rovo-spaces/services/useListSpaces';
import { useRenameSpace } from '@atlassian/rovo-spaces/services/useRenameSpace';
import { DeleteSpaceModal } from '@atlassian/rovo-spaces/ui/DeleteSpaceModal';
import { createEmojiProvider } from '@atlassian/rovo-spaces/utils/emoji-provider';

import { Z_INDEX_POPUP_COMPONENTS } from '../../../common/constants';
import type { ConversationListProps } from '../../../common/types';
import { ButtonMenuItem, type ButtonMenuItemProps } from '../../../common/ui/button-menu-item';
import { LinkMenuItem } from '../../../common/ui/link-menu-item';

import { GenerativeSpaceIcon } from './generative-space-icon';
import messages from './messages';

const SPACES_LIMIT = 5;

export type SpaceMenuItemsProps = Pick<
	ConversationListProps,
	| 'onConversationRename'
	| 'onConversationDelete'
	| 'onConversationPinToggle'
	| 'pinnedConversationSummary'
	| 'conversations'
	| 'onSelectConversation'
	| 'currentConversation'
> & {
	cloudId: string;
	isADSNavigationEnabled: boolean;
	onSelectSpace?: (space: Space) => void;
	selectedSpaceId?: string;
	product: string;
	experienceId: string;
	isNewAllConversationScreenEnabled?: boolean;
	isSpinnerOverlayConversationIconEnabled?: boolean;
	onSelectRecommendedSpaces?: () => void;
	isRecommendedSpacesSelected?: boolean;
	isViewAllSpacesSelected?: boolean;
	onSpaceDelete?: (space: Space) => void;
	/** Called when the user confirms a rename. newName is the trimmed new name. */
	onSpaceRename?: (space: Space, newName: string) => void;
	/**
	 * The host's "start a new chat" handler — same callback as the new-chat
	 * button at the top of the side nav. When the user successfully deletes
	 * the space they are currently viewing, this is invoked automatically so
	 * the host returns the user to a fresh chat (rather than leaving them on
	 * a now-deleted view). Hosts that don't pass `selectedSpaceId` will not
	 * trigger this behaviour.
	 */
	onNewChatButtonClick?: ButtonMenuItemProps['onClick'];
	/**
	 * Called after a space is successfully created via the Create Space modal.
	 *
	 * **Contract:** the HOST owns the optimistic store update. This component
	 * does NOT call `addSpace` itself — it just forwards the event upward.
	 * See the matching docstring on `RovoSideNavigationProps.onSpaceCreated`
	 * for the full contract and the regression-bug context.
	 */
	onSpaceCreated?: (
		space: import('@atlassian/rovo-spaces/controllers/space-store/types').CreateSpaceResponse,
		emojiId?: string,
	) => void;
	/**
	 * Called when the user clicks the "View all Rovo spaces" link rendered as a
	 * sibling under the Spaces section (mirrors the existing "View all agents"
	 * pattern in `agents-items`).
	 *
	 * Rendering of the link is gated INSIDE this component by the Rovo spaces
	 * experiment (not by the presence of this callback), so hosts can pass it
	 * unconditionally without risking accidental exposure of an ungated feature.
	 * When the experiment is off, the link is hidden even if a callback is provided.
	 */
	onViewAllSpacesClick?: () => void;
	renderRecommendedSpacesItem?: (item: React.ReactNode) => React.ReactNode;
};

export const SpaceMenuItems = ({
	cloudId,
	isADSNavigationEnabled,
	onSelectSpace,
	selectedSpaceId,
	product,
	experienceId,
	onSelectRecommendedSpaces,
	isRecommendedSpacesSelected = false,
	onSpaceDelete,
	onSpaceRename,
	onNewChatButtonClick,
	onViewAllSpacesClick,
	renderRecommendedSpacesItem,
}: SpaceMenuItemsProps): JSX.Element => {
	const { formatMessage } = useIntl();
	const { spaces, isLoading } = useListSpaces({ cloudId, product, experienceId });
	const emojiProvider = useMemo(() => createEmojiProvider(cloudId), [cloudId]);
	const limitedSpaces = spaces.slice(0, SPACES_LIMIT);

	if (isLoading) {
		return (
			<React.Fragment>
				<Spinner size="small" label={formatMessage(messages.loadingLabel)} />
			</React.Fragment>
		);
	}

	const recommendedSpacesItem = (
		<LinkMenuItem
			isADSNavigationEnabled={isADSNavigationEnabled}
			href="#"
			isSelected={isRecommendedSpacesSelected}
			testId="rovo-chat-sidenav-recommended-spaces"
			onClick={(e) => {
				e.preventDefault();
				onSelectRecommendedSpaces?.();
			}}
			elemBefore={<GenerativeSpaceIcon />}
		>
			{formatMessage(messages.recommendedSpaces)}
		</LinkMenuItem>
	);

	const renderedRecommendedSpacesItem = renderRecommendedSpacesItem
		? renderRecommendedSpacesItem(recommendedSpacesItem)
		: recommendedSpacesItem;

	return (
		<React.Fragment>
			{renderedRecommendedSpacesItem}
			{limitedSpaces.map((space) => (
				<SpaceMenuItem
					key={space.id}
					space={space}
					cloudId={cloudId}
					product={product}
					experienceId={experienceId}
					isSelected={space.id === selectedSpaceId}
					isADSNavigationEnabled={isADSNavigationEnabled}
					onSelectSpace={onSelectSpace}
					emojiProvider={emojiProvider}
					onSpaceDelete={onSpaceDelete}
					onSpaceRename={onSpaceRename}
					onNewChatButtonClick={onNewChatButtonClick as (() => void) | undefined}
				/>
			))}
			{onViewAllSpacesClick &&
				FeatureGates.getExperimentValue('rovo_chat_spaces_experiment', 'isEnabled', false) && (
					<ButtonMenuItem
						isADSNavigationEnabled={isADSNavigationEnabled}
						onClick={() => {
							onViewAllSpacesClick();
						}}
						elemBefore={<ViewAllIcon label="" />}
					>
						{formatMessage(messages.viewAllSpacesLabel)}
					</ButtonMenuItem>
				)}
		</React.Fragment>
	);
};

type SpaceMenuItemProps = {
	space: Space;
	cloudId: string;
	product: string;
	experienceId: string;
	isSelected: boolean;
	isADSNavigationEnabled: boolean;
	onSelectSpace?: (space: Space) => void;
	emojiProvider: Promise<EmojiProvider>;
	onSpaceDelete?: (space: Space) => void;
	/** Called when the user confirms a rename. newName is the trimmed new name. */
	onSpaceRename?: (space: Space, newName: string) => void;
	/**
	 * Host's "start a new chat" handler. Invoked automatically after a successful
	 * delete IF this menu item represents the currently-selected space, so the
	 * user is taken off the now-deleted view.
	 */
	onNewChatButtonClick?: () => void;
};

const SPACE_TESTID_PREFIX = 'rovo-nav-space-item';

const renameStyles = cssMap({
	renameForm: {
		display: 'flex',
		border: `${token('border.width')} solid transparent`,
	},
	innerForm: {
		width: '100%',
		display: 'flex',
		alignItems: 'center',
		gap: token('space.050'),
	},
	textFieldContainer: {
		flexGrow: 1,
		marginTop: token('space.negative.100'),
	},
	textField: {
		width: '100%',
		boxSizing: 'border-box',
		font: token('font.body'),
		fontWeight: token('font.weight.medium'),
		paddingTop: token('space.050'),
		paddingRight: token('space.075'),
		paddingBottom: token('space.050'),
		paddingLeft: token('space.050'),
		border: `${token('border.width')} solid ${token('color.border.input')}`,
		borderRadius: token('radius.small'),
		outline: 'none',
		'&:focus': {
			borderColor: token('color.border.focused'),
			// @ts-expect-error - setting box shadow to match ADS
			boxShadow: `0 0 0 ${token('border.width')} ${token('color.border.focused')}`,
		},
	},
});

const spaceItemStyles = cssMap({
	emojiTile: {
		flexShrink: 0,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '16px',
		height: '16px',
	},
});

const SpaceMenuItem = ({
	space,
	cloudId,
	product,
	experienceId,
	isSelected,
	isADSNavigationEnabled,
	onSelectSpace,
	emojiProvider,
	onSpaceDelete,
	onSpaceRename,
	onNewChatButtonClick,
}: SpaceMenuItemProps): JSX.Element => {
	const { formatMessage } = useIntl();
	const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
	const [isEditing, setIsEditing] = useState(false);
	const [localSpaceName, setLocalSpaceName] = useState(space.name);
	const formRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLInputElement>(null);

	const { deleteSpace } = useDeleteSpace({
		cloudId,
		product,
		experienceId,
		onSuccess: () => {
			// Notify the host first so any host-side bookkeeping (analytics,
			// pubsub, etc.) runs before we trigger the navigation reset below.
			onSpaceDelete?.(space);
			// If the user just deleted the space they were viewing, return them
			// to a fresh chat using the host's existing new-chat handler. This
			// reuses whatever side-effects the host already implements for the
			// new-chat button (e.g. publishing `space-deselected`, navigating
			// to the chat root, etc.) so each host gets the right UX without
			// having to wire any additional callback.
			if (isSelected) {
				onNewChatButtonClick?.();
			}
		},
	});

	const { renameSpace } = useRenameSpace({
		cloudId,
		product,
		experienceId,
		onSuccess: (updatedSpace, newName) => onSpaceRename?.(updatedSpace, newName),
	});

	const onRenameBtnClicked = useCallback(() => {
		setIsMoreMenuOpen(false);
		setLocalSpaceName(space.name);
		setIsEditing(true);
	}, [space.name]);

	const onSubmitRename = useCallback(
		(value: string) => {
			const trimmed = value.trim();
			if (!trimmed) {
				setIsEditing(false);
				return;
			}
			setIsEditing(false);
			void renameSpace(space.id, trimmed);
		},
		[space.id, renameSpace],
	);

	const onCancelRename = useCallback(() => {
		setIsEditing(false);
	}, []);

	useEffect(() => {
		if (!isEditing) {
			setLocalSpaceName(space.name);
		}
	}, [isEditing, space.name]);

	useEffect(() => {
		if (isEditing) {
			inputRef.current?.focus();
		}
	}, [isEditing]);

	useEffect(() => {
		if (!isEditing) {
			return;
		}
		const handleClickOutside = (event: Event) => {
			if (formRef.current && !formRef.current.contains(event.target as Node)) {
				onCancelRename();
			}
		};
		const unbind = bind(window, {
			type: 'mousedown',
			listener: handleClickOutside,
			options: { capture: true },
		});
		return unbind;
	}, [isEditing, onCancelRename]);

	const onSpaceItemClick = useCallback(
		(event: React.MouseEvent) => {
			event.preventDefault();
			onSelectSpace?.(space);
		},
		[onSelectSpace, space],
	);

	const icon = space.emoji ? (
		<Box xcss={spaceItemStyles.emojiTile} aria-hidden="true">
			<ResourcedEmoji
				emojiId={{ id: space.emoji, shortName: '' }}
				emojiProvider={emojiProvider}
				fitToHeight={16}
			/>
		</Box>
	) : (
		<FolderClosedIcon label="" />
	);

	const moreButton = (
		<Popup
			testId={`${SPACE_TESTID_PREFIX}-more-popup-${space.id}`}
			isOpen={isMoreMenuOpen}
			onClose={() => setIsMoreMenuOpen(false)}
			zIndex={Z_INDEX_POPUP_COMPONENTS}
			placement="bottom-end"
			content={() => (
				<MenuGroup>
					<ButtonItem
						testId={`${SPACE_TESTID_PREFIX}-rename-${space.id}`}
						iconBefore={<EditIcon label="" />}
						onClick={onRenameBtnClicked}
					>
						{formatMessage(messages.moreMenuRenameLabel)}
					</ButtonItem>
					<ButtonItem
						testId={`${SPACE_TESTID_PREFIX}-delete-${space.id}`}
						iconBefore={<DeleteIcon label="" color={token('color.icon.danger')} />}
						onClick={() => {
							setIsMoreMenuOpen(false);
							setIsDeleteModalOpen(true);
						}}
					>
						{formatMessage(messages.moreMenuDeleteLabel)}
					</ButtonItem>
				</MenuGroup>
			)}
			trigger={(triggerProps) => (
				<IconButton
					{...triggerProps}
					testId={`${SPACE_TESTID_PREFIX}-more-${space.id}`}
					icon={() => <MoreIcon label="" size="small" />}
					appearance="subtle"
					spacing="compact"
					onClick={(e) => {
						e.preventDefault();
						e.stopPropagation();
						setIsMoreMenuOpen((prev) => !prev);
					}}
					label={formatMessage(messages.moreMenuLabel)}
				/>
			)}
		/>
	);

	// Shared inner content for both form variants
	const renameFormInner = (
		<React.Fragment>
			<Box xcss={renameStyles.textFieldContainer}>
				<Field name="spaceName" defaultValue={localSpaceName}>
					{({ fieldProps }) => (
						<input
							{...fieldProps}
							ref={inputRef}
							css={renameStyles.textField}
							aria-label={formatMessage(messages.moreMenuRenameLabel)}
							onKeyDown={(e: React.KeyboardEvent) => {
								if (e.key === 'Escape') {
									onCancelRename();
								}
							}}
							data-testid={`${SPACE_TESTID_PREFIX}-name-edit-${space.id}`}
						/>
					)}
				</Field>
			</Box>
			<IconButton
				icon={(iconProps) => (
					<CheckMarkIcon {...iconProps} color={token('color.icon.accent.green')} />
				)}
				type="submit"
				appearance="subtle"
				shape="circle"
				spacing="compact"
				label={formatMessage(messages.confirmRenameLabel)}
				testId={`${SPACE_TESTID_PREFIX}-rename-confirm-${space.id}`}
			/>
			<IconButton
				icon={(iconProps) => <CrossIcon {...iconProps} color={token('color.icon.accent.red')} />}
				type="button"
				onClick={onCancelRename}
				appearance="subtle"
				shape="circle"
				spacing="compact"
				label={formatMessage(messages.cancelRenameLabel)}
				testId={`${SPACE_TESTID_PREFIX}-rename-cancel-${space.id}`}
			/>
		</React.Fragment>
	);

	const renameForm = (
		<div css={renameStyles.renameForm} ref={formRef}>
			{fg('platform-design_system_team-form_conversion') ? (
				<Form
					onSubmit={(data: { spaceName: string }) => {
						onSubmitRename(data.spaceName);
					}}
					formProps={{ class: renameStyles.innerForm }}
				>
					{renameFormInner}
				</Form>
			) : (
				<Form
					onSubmit={(data: { spaceName: string }) => {
						onSubmitRename(data.spaceName);
					}}
				>
					{({ formProps }) => (
						<form {...formProps} css={renameStyles.innerForm}>
							{renameFormInner}
						</form>
					)}
				</Form>
			)}
		</div>
	);

	return (
		<React.Fragment>
			{/* DeleteSpaceModal is always mounted so its state is preserved regardless of isEditing */}
			<DeleteSpaceModal
				space={space}
				isOpen={isDeleteModalOpen}
				onClose={() => setIsDeleteModalOpen(false)}
				onDeleteConfirm={deleteSpace}
			/>
			{isEditing ? (
				renameForm
			) : (
				<React.Fragment>
					<LinkMenuItem
						isADSNavigationEnabled={isADSNavigationEnabled}
						href="#"
						isSelected={isSelected}
						onClick={onSpaceItemClick}
						elemBefore={icon}
						actionsOnHover={moreButton}
					>
						{localSpaceName}
					</LinkMenuItem>
				</React.Fragment>
			)}
		</React.Fragment>
	);
};
